
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SocialMediaLinks {
  facebook: string;
  instagram: string;
  twitter: string;
  youtube: string;
  linkedin: string;
  tiktok: string;
  whatsapp: string;
}

const SocialMediaSettings = () => {
  const { toast } = useToast();
  const [socialLinks, setSocialLinks] = useState<SocialMediaLinks>({
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: '',
    linkedin: '',
    tiktok: '',
    whatsapp: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (platform: keyof SocialMediaLinks) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSocialLinks(prev => ({
      ...prev,
      [platform]: e.target.value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      localStorage.setItem('socialMediaLinks', JSON.stringify(socialLinks));
      
      toast({
        title: "Settings saved",
        description: "Your social media links have been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save social media settings.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Social Media Integration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <Facebook className="h-5 w-5 text-blue-600" />
            <Input
              placeholder="Facebook Page URL"
              value={socialLinks.facebook}
              onChange={handleInputChange('facebook')}
              className="flex-1"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <Instagram className="h-5 w-5 text-pink-600" />
            <Input
              placeholder="Instagram Profile URL"
              value={socialLinks.instagram}
              onChange={handleInputChange('instagram')}
              className="flex-1"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <Twitter className="h-5 w-5 text-blue-400" />
            <Input
              placeholder="X (Twitter) Profile URL"
              value={socialLinks.twitter}
              onChange={handleInputChange('twitter')}
              className="flex-1"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <Youtube className="h-5 w-5 text-red-600" />
            <Input
              placeholder="YouTube Channel URL"
              value={socialLinks.youtube}
              onChange={handleInputChange('youtube')}
              className="flex-1"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <Linkedin className="h-5 w-5 text-blue-700" />
            <Input
              placeholder="LinkedIn Profile URL"
              value={socialLinks.linkedin}
              onChange={handleInputChange('linkedin')}
              className="flex-1"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <svg 
              className="h-5 w-5 text-gray-900 flex-shrink-0" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M9 12a9 9 0 0 0 6 8.5c3.9-2 6-5.5 6-8.5A9 9 0 0 0 9 3.5C5.1 5.5 3 9 3 12c0 3 2.1 6.5 6 8.5Z" />
              <path d="M9 12c0 2 1.6 5 4 6.5c2.4-1.5 4-4.5 4-6.5a9 9 0 0 0-4-7.5C9.6 7 8 10 8 12Z" />
            </svg>
            <Input
              placeholder="TikTok Profile URL"
              value={socialLinks.tiktok}
              onChange={handleInputChange('tiktok')}
              className="flex-1"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <MessageCircle className="h-5 w-5 text-green-500" />
            <Input
              placeholder="WhatsApp Business URL"
              value={socialLinks.whatsapp}
              onChange={handleInputChange('whatsapp')}
              className="flex-1"
            />
          </div>
        </div>

        <Button 
          onClick={handleSave}
          disabled={isLoading}
          className="w-full"
        >
          Save Social Media Settings
        </Button>
      </CardContent>
    </Card>
  );
};

export default SocialMediaSettings;
